import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Bell, UserCheck, UserX, Calendar, Users } from 'lucide-react';
import { gameParticipantsService, PlayerRequest } from '@/services/gameParticipantsService';
import { useUserRole } from '@/hooks/useUserRole';
import { toast } from 'sonner';

export const PlayerRequestNotifications = () => {
  const [requests, setRequests] = useState<PlayerRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const { userId, permissions } = useUserRole();

  useEffect(() => {
    if (userId && (permissions.isCoach || permissions.isTeamOwner)) {
      loadRequests();
    }
  }, [userId, permissions.isCoach, permissions.isTeamOwner]);

  const loadRequests = async () => {
    if (!userId) return;
    
    try {
      setLoading(true);
      const pendingRequests = await gameParticipantsService.getPendingPlayerRequests(userId);
      setRequests(pendingRequests);
    } catch (error) {
      console.error('Error loading player requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestResponse = async (requestId: string, decision: 'approved' | 'denied', playerName: string) => {
    if (!userId) return;

    try {
      const success = await gameParticipantsService.respondToPlayerRequest(requestId, decision, userId);
      
      if (success) {
        toast.success(
          `Player request ${decision}! ${playerName} has been ${decision === 'approved' ? 'approved to join' : 'denied from joining'} the game.`
        );
        // Remove the request from the list
        setRequests(prev => prev.filter(req => req.id !== requestId));
      } else {
        toast.error('Failed to respond to player request');
      }
    } catch (error) {
      console.error('Error responding to request:', error);
      toast.error('Failed to respond to player request');
    }
  };

  // Don't show component if user can't manage teams or there are no requests
  if (!permissions.isCoach && !permissions.isTeamOwner) {
    return null;
  }

  if (requests.length === 0) {
    return null;
  }

  return (
    <Card className="border-blue-500/20 bg-blue-500/5">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-blue-600">
          <Bell className="w-5 h-5" />
          Player Requests ({requests.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          requests.slice(0, 3).map((request) => (
            <div key={request.id} className="flex items-center justify-between p-3 bg-background rounded-lg border">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-muted-foreground" />
                  <span className="font-medium">{request.player_name}</span>
                  <Badge variant="outline" className="text-xs">
                    {request.team_side} team
                  </Badge>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Calendar className="w-3 h-3" />
                  <span>{request.game_title}</span>
                </div>
                <span className="text-xs text-muted-foreground">
                  Requested {new Date(request.requested_at).toLocaleDateString()}
                </span>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleRequestResponse(request.id, 'approved', request.player_name)}
                  className="text-green-600 border-green-600 hover:bg-green-600 hover:text-white"
                >
                  <UserCheck className="w-3 h-3 mr-1" />
                  Approve
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleRequestResponse(request.id, 'denied', request.player_name)}
                  className="text-red-600 border-red-600 hover:bg-red-600 hover:text-white"
                >
                  <UserX className="w-3 h-3 mr-1" />
                  Deny
                </Button>
              </div>
            </div>
          ))
        )}
        {requests.length > 3 && (
          <p className="text-sm text-muted-foreground text-center">
            and {requests.length - 3} more requests...
          </p>
        )}
      </CardContent>
    </Card>
  );
};